/**
 * Per-harness install commands for the Hotline integration MCP.
 *
 * The connection string IS the whole configuration — a streamable-HTTP MCP URL
 * with the token in the path, so it works in every harness including those with
 * no custom-header support:
 *
 *   https://api.hotline.red/mcp/hmcp_…
 *
 * `?lang=` is reserved on that URL for localized tool output later; it is
 * accepted today and English is served regardless.
 */

export interface HarnessCommand {
  id: 'claude-code' | 'cursor' | 'vscode' | 'gemini-cli' | 'codex' | 'generic';
  label: string;
  /** 'bash' for a runnable command; 'json'/'toml' for config to paste. */
  language: 'bash' | 'json' | 'toml';
  /** Where the config lives, when it is a file rather than a command. */
  hint?: string;
  code: string;
}

/** Compose the connection string for a raw `hmcp_*` token. */
export function mcpConnectionString(apiUrl: string, token: string): string {
  return `${apiUrl.replace(/\/+$/, '')}/mcp/${token}`;
}

/** Every harness command, rendered for one connection string. */
export function harnessCommands(connectionString: string): HarnessCommand[] {
  return [
    {
      id: 'claude-code',
      label: 'Claude Code',
      language: 'bash',
      code: `claude mcp add --transport http hotline "${connectionString}"`,
    },
    {
      id: 'cursor',
      label: 'Cursor',
      language: 'json',
      hint: '.cursor/mcp.json (project) or ~/.cursor/mcp.json (global)',
      code: JSON.stringify({ mcpServers: { hotline: { url: connectionString } } }, null, 2),
    },
    {
      id: 'vscode',
      label: 'VS Code',
      language: 'bash',
      code: `code --add-mcp '${JSON.stringify({ name: 'hotline', type: 'http', url: connectionString })}'`,
    },
    {
      id: 'gemini-cli',
      label: 'Gemini CLI',
      language: 'bash',
      code: `gemini mcp add hotline "${connectionString}" -t http`,
    },
    {
      id: 'codex',
      label: 'Codex CLI',
      language: 'toml',
      hint: '~/.codex/config.toml',
      code: `[mcp_servers.hotline]\nurl = "${connectionString}"`,
    },
    {
      id: 'generic',
      label: 'Any MCP client',
      language: 'json',
      hint: 'Streamable HTTP transport; no headers required — the token is in the URL.',
      code: JSON.stringify({ url: connectionString }, null, 2),
    },
  ];
}
